import { useSignOutMutation } from "@/modules/auth/services";
import { setIsAuth } from "@/modules/auth/state";
import { AppPaths } from "@/shared/common/enums";
import { Notification, UnsavedChangesModal, } from "@/shared/components";
import { clearUserFromStorage } from "@/shared/helpers";
import {
  useAppDispatch,
  useAppSelector,
  useBeforeUnload,
  useUnsavedChangesPrompt
} from "@/shared/hooks";
import { setHasUnsavedChanges, setPendingSignOut } from "@/shared/state/common";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { type ReactElement, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface Props {
  title?: string,
  children: ReactElement,
  isEdit?: boolean;
  isSaved: boolean;
  hasChanges?: boolean;
  className?: string;
  canClose?: boolean;
}

export const BusinessLayout = (
  {
    title,
    isSaved,
    canClose,
    children,
    className,
    isEdit = false,
    hasChanges = false,
  }: Props,
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [signOut] = useSignOutMutation();
  const pendingSignOut = useAppSelector((state) => state.common.pendingSignOut);

  const { errors } = useFormProps();

  const shouldBlock = !isSaved && hasChanges && isEdit;

  useBeforeUnload({ shouldBlock });
  const { showModal, cancelLeave, confirmLeave } = useUnsavedChangesPrompt({ shouldBlock });

  const handleSignOutConfirm = () => {
    signOut()
      .unwrap()
      .then(() => {
        clearUserFromStorage();
        dispatch(setIsAuth(false));
        dispatch(setPendingSignOut(false));
        navigate(AppPaths.Login);
      });
  };

  const handleSignOutCancel = () => {
    dispatch(setPendingSignOut(false));
  };

  const isModalOpen = showModal || pendingSignOut;
  const onConfirm = pendingSignOut ? handleSignOutConfirm : confirmLeave;
  const onClose = pendingSignOut ? handleSignOutCancel : cancelLeave;

  useEffect(() => {
    dispatch(setHasUnsavedChanges(shouldBlock));
    return () => {
      dispatch(setHasUnsavedChanges(false));
    };
  }, [shouldBlock]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!errors || !Object.keys(errors).length) return;

    const flattenErrorKeys = (obj: Record<string, unknown>, prefix = ""): Set<string> => {
      const keys = new Set<string>();
      Object.keys(obj).forEach((key) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const val = obj[key];
        if (val && typeof val === "object" && !("message" in val) && !("type" in val)) {
          flattenErrorKeys(val as Record<string, unknown>, fullKey).forEach((k) => keys.add(k));
        } else {
          keys.add(fullKey);
        }
      });
      return keys;
    };

    const errorFieldNames = flattenErrorKeys(errors as Record<string, unknown>);
    const namedElements = document.querySelectorAll("[name]");
    for (const el of namedElements) {
      const name = el.getAttribute("name");
      if (name && errorFieldNames.has(name)) {
        let target: Element = el;
        while (target.parentElement && (target as HTMLElement).offsetParent === null) {
          target = target.parentElement;
        }
        (target as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" });
        break;
      }
    }
  }, [errors]);

  return (
    <div className={`full-width page-body ${className}`}>
      <div className="full-width flexbox">
        <div className="main-section">
          <div className="main-section-content">
            <Heading
              size="xsmall"
              weight="bolder"
              className="mb-32"
            >
              {title}
            </Heading>
            <Notification canClose={canClose}/>
            {children}
            <UnsavedChangesModal
              isOpen={isModalOpen}
              onConfirm={onConfirm}
              onClose={onClose}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
